/* eslint-disable @typescript-eslint/no-empty-function */
import React, { useReducer, useEffect } from 'react';
import { auth } from 'services/firebase';

type State = {
  user: firebase.User | null;
  isAuthenticated: boolean;
  loading: boolean;
};

type Action =
  | {
      type: 'setUser';
      payload: {
        user: firebase.User | null;
        loading: boolean;
        isAuthenticated: boolean;
      };
    }
  | {
      type: 'setLoading';
      payload: {
        loading: boolean;
      };
    };

type Context = {
  state: State;
  dispatch: React.Dispatch<Action>;
  dispatchLoading: (loading: boolean) => void;
  dispatchSignInSuccess: (user: firebase.User) => void;
  dispatchSignInFailure: () => void;
};

const initialState: State = {
  user: null,
  isAuthenticated: false,
  loading: true,
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'setUser': {
      const { user, isAuthenticated, loading } = action.payload;
      return { ...state, user, isAuthenticated, loading };
    }
    case 'setLoading': {
      return { ...state, loading: action.payload.loading };
    }
    default:
      return { ...state };
  }
};

export const AuthContext = React.createContext<Context>({
  state: initialState,
  dispatch: () => {},
  dispatchLoading: () => {},
  dispatchSignInSuccess: () => {},
  dispatchSignInFailure: () => {},
});

export const AuthContextProvider = ({ children }: any): JSX.Element => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const dispatchLoading = (loading: boolean): void => {
    dispatch({ type: 'setLoading', payload: { loading } });
  };

  const dispatchSignInSuccess = (user: firebase.User): void => {
    dispatch({
      type: 'setUser',
      payload: {
        user,
        isAuthenticated: true,
        loading: false,
      },
    });
  };

  const dispatchSignInFailure = (): void => {
    dispatch({
      type: 'setUser',
      payload: {
        user: null,
        isAuthenticated: false,
        loading: false,
      },
    });
  };

  useEffect(() => {
    auth.onAuthStateChanged((user: firebase.User | null) => {
      if (user) {
        dispatchSignInSuccess(user);
      } else {
        dispatchSignInFailure();
      }
    });
  }, []);

  return (
    <AuthContext.Provider
      value={{
        state,
        dispatch,
        dispatchSignInSuccess,
        dispatchSignInFailure,
        dispatchLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
